import React, { useState, useEffect, useRef } from 'react';
/* eslint-disable no-console */
import Html from '~/commons/components/Html.jsx';

/**
 * FadeOutContent Component
 * Displays an image that fades out when clicked, revealing the HTML content underneath
 */
export const FadeOutContent = ({ img, content }) => {
  const [isFading, setIsFading] = useState(false);
  const [imageRemoved, setImageRemoved] = useState(false);
  const TRANS_MS = 2000;
  const contentRef = useRef(null);
  const [coverHeight, setCoverHeight] = useState(null);

  useEffect(() => {
    if (!isFading) return undefined;
    const t = setTimeout(() => {
      setImageRemoved(true);
    }, TRANS_MS + 100);
    return () => clearTimeout(t);
  }, [isFading]);

  useEffect(() => {
    if (!contentRef || !contentRef.current) return undefined;
    const update = () => {
      try {
        setCoverHeight(contentRef.current.scrollHeight || 0);
      } catch (e) {
        setCoverHeight(null);
      }
    };
    update();
    const ro = new ResizeObserver(() => update());
    ro.observe(contentRef.current);
    window.addEventListener('orientationchange', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('orientationchange', update);
    };
  }, [contentRef]);

  const handleClick = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setIsFading(true));
    });
  };
  return (
    <div className="animated-content fade-out-wrapper" style={{ position: 'relative', marginBottom: '1rem' }}>
      {!imageRemoved && img && (
        <div
          className="fade-out-image"
          onClick={handleClick}
          style={{
            cursor: 'pointer',
            pointerEvents: 'auto',
            position: 'absolute',
            willChange: 'opacity',
            top: 0,
            left: 0,
            width: '100%',
            height: coverHeight ? `${coverHeight}px` : 'auto',
            overflow: 'hidden',
            zIndex: 2,
          }}
        >
          <img
            src={img}
            alt="Click to reveal content"
            style={{
              width: '100%',
              height: '100%',
              display: 'block',
              objectFit: 'cover',
              transition: `opacity ${TRANS_MS}ms ease-out`,
              opacity: isFading ? 0 : 1,
            }}
          />
        </div>
      )}
      <div
        ref={contentRef}
        className="fade-out-content"
        style={{
          position: 'relative',
          top: 0,
          left: 0,
          width: '100%',
          opacity: 1,
          pointerEvents: 'auto',
          zIndex: 1,
        }}
      >
        <Html>{content}</Html>
      </div>
    </div>
  );
};

export const SlideDownContent = ({ img, content }) => {
  const [isSliding, setIsSliding] = useState(false);
  const [contentHeight, setContentHeight] = useState(0);
  const [coverHeight, setCoverHeight] = useState(null);
  const [imageRemoved, setImageRemoved] = useState(false);
  const [slideMs, setSlideMs] = useState(2000);
  const contentRef = React.useRef(null);
  const imageRef = React.useRef(null);

  useEffect(() => {
    if (!contentRef.current) return undefined;
    const update = () => {
      try {
        setCoverHeight(contentRef.current.scrollHeight || 0);
      } catch (e) {
        setCoverHeight(null);
      }
    };
    update();
    const ro = new ResizeObserver(() => update());
    ro.observe(contentRef.current);
    return () => ro.disconnect();
  }, [contentRef]);

  const handleClick = () => {
    if (contentRef.current && imageRef.current) {
      const imageHeight = imageRef.current.offsetHeight;
      const contentFullHeight = contentRef.current.scrollHeight;
      const distance = contentFullHeight + imageHeight;
      setContentHeight(distance);
      const computedMs = Math.max(800, Math.min(7000, Math.round(distance * 2.5)));
      console.log('SlideDownContent: distance=', distance, 'computedMs=', computedMs);
      setSlideMs(computedMs);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setIsSliding(true));
      });
      return;
    }
    setIsSliding(true);
  };

  useEffect(() => {
    if (!isSliding) return undefined;
    const t = setTimeout(() => {
      console.log('SlideDownContent: fallback timeout fired - removing image');
      setImageRemoved(true);
    }, slideMs + 200);
    return () => clearTimeout(t);
  }, [isSliding, slideMs]);

  return (
    <div className="animated-content slide-down-wrapper" style={{ position: 'relative', overflow: 'hidden', zIndex: 0 }}>
      <div
        ref={contentRef}
        className="slide-down-content"
        style={{
          position: 'relative',
          zIndex: 1,
          visibility: 'visible',
        }}
      >
        <Html>{content}</Html>
      </div>
      {img && !imageRemoved && (
        <div
          ref={imageRef}
          className={`slide-down-image ${isSliding ? 'sliding' : ''}`}
          onClick={handleClick}
          onTransitionEnd={(e) => {
            if (e && e.propertyName !== 'transform') return;
            if (isSliding) {
              console.log('SlideDownContent: transitionend - removing image');
              setImageRemoved(true);
            }
          }}
          style={{
            cursor: 'pointer',
            transition: `transform ${slideMs}ms ease-out, opacity 500ms ease-out ${Math.max(0, slideMs - 500)}ms`,
            transform: isSliding ? `translateY(${contentHeight}px)` : 'translateY(0)',
            opacity: isSliding ? 0 : 1,
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: coverHeight ? `${coverHeight}px` : 'auto',
            zIndex: 3,
          }}
        >
          <img
            src={img}
            alt="Click to reveal content"
            style={{
              width: '100%',
              height: '100%',
              display: 'block',
              objectFit: 'cover',
            }}
          />
        </div>
      )}
    </div>
  );
};

const AnimatedContent = ({ animation, img, content }) => {
  if (animation === 'fade-out') {
    return <FadeOutContent img={img} content={content} />;
  }

  if (animation === 'slide-down') {
    return <SlideDownContent img={img} content={content} />;
  }

  // No animation, just display content
  return <Html>{content}</Html>;
};

export default AnimatedContent;
